import { useState } from 'react';
import { ComputerDesktopIcon, DevicePhoneMobileIcon, DeviceTabletIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Props {
  sessions: { device_type: string; browser: string | null; browser_version?: string | null; os: string | null; os_version?: string | null; device_vendor?: string | null; country?: string | null; city?: string | null; language?: string | null; locale?: string | null }[];
}

function countBy<T>(arr: T[], key: keyof T): Record<string, number> {
  return arr.reduce((acc, item) => {
    const val = String(item[key] ?? 'Unknown');
    acc[val] = (acc[val] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

const PAGE_SIZE = 8;

function PaginatedList({
  items,
  total,
  barColor,
}: {
  items: [string, number][];
  total: number;
  barColor: string;
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(items.length / PAGE_SIZE);
  const visible = items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div>
      <div className="space-y-3">
        {visible.map(([label, count]) => (
          <div key={label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 truncate pr-2">{label}</span>
              <span className="text-gray-500 flex-shrink-0">
                {count.toLocaleString()} &middot; {((count / total) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${(count / total) * 100}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, items.length)} of {items.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-3.5 h-3.5 text-gray-500" />
            </button>
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-5 h-5 text-xs rounded transition-colors ${
                  i === page
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page === pageCount - 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const deviceIcons: Record<string, React.FC<{ className?: string }>> = {
  mobile: DevicePhoneMobileIcon,
  tablet: DeviceTabletIcon,
  desktop: ComputerDesktopIcon,
};

export default function DeviceBreakdown({ sessions }: Props) {
  const deviceCounts = countBy(sessions, 'device_type');
  const browserCounts = countBy(sessions, 'browser');
  const osCounts = countBy(sessions, 'os');
  const vendorCounts = countBy(sessions, 'device_vendor');
  const countryCounts = countBy(sessions, 'country');
  const cityCounts = countBy(sessions, 'city');
  const languageCounts = countBy(sessions, 'language');
  const total = sessions.length || 1;

  const sortKnown = (entries: [string, number][]) =>
    entries.sort((a, b) => {
      if (a[0] === 'Unknown') return 1;
      if (b[0] === 'Unknown') return -1;
      return b[1] - a[1];
    });

  const devices = Object.entries(deviceCounts).sort((a, b) => b[1] - a[1]);
  const browsers = Object.entries(browserCounts).sort((a, b) => b[1] - a[1]);
  const oses = Object.entries(osCounts).sort((a, b) => b[1] - a[1]);
  const vendors = sortKnown(Object.entries(vendorCounts));
  const countries = sortKnown(Object.entries(countryCounts));
  const cities = sortKnown(Object.entries(cityCounts));
  const languages = sortKnown(Object.entries(languageCounts));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Devices</h3>
          <div className="space-y-3">
            {devices.map(([device, count]) => {
              const Icon = deviceIcons[device] ?? ComputerDesktopIcon;
              return (
                <div key={device} className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize text-gray-700">{device}</span>
                      <span className="text-gray-500">
                        {count.toLocaleString()} &middot; {((count / total) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(count / total) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Browsers</h3>
          <PaginatedList items={browsers} total={total} barColor="#60a5fa" />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Operating Systems</h3>
          <PaginatedList items={oses} total={total} barColor="#2dd4bf" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Device Brands</h3>
            {vendors.length > 0 && (
              <span className="text-xs text-gray-400">{vendors.length} total</span>
            )}
          </div>
          {vendors.length > 0 ? (
            <PaginatedList items={vendors} total={total} barColor="#fb923c" />
          ) : (
            <p className="text-sm text-gray-400">No brand data available</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Countries</h3>
            {countries.length > 0 && (
              <span className="text-xs text-gray-400">{countries.length} total</span>
            )}
          </div>
          {countries.length > 0 ? (
            <PaginatedList items={countries} total={total} barColor="#38bdf8" />
          ) : (
            <p className="text-sm text-gray-400">No country data available</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Cities</h3>
            {cities.length > 0 && (
              <span className="text-xs text-gray-400">{cities.length} total</span>
            )}
          </div>
          {cities.length > 0 ? (
            <PaginatedList items={cities} total={total} barColor="#34d399" />
          ) : (
            <p className="text-sm text-gray-400">No city data available</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Languages</h3>
            {languages.length > 0 && (
              <span className="text-xs text-gray-400">{languages.length} total</span>
            )}
          </div>
          {languages.length > 0 ? (
            <PaginatedList items={languages} total={total} barColor="#a78bfa" />
          ) : (
            <p className="text-sm text-gray-400">No language data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
